import { staleTimes } from '@platform/api-client';
import type { Operation, ResponseBody, TypedClient } from '@platform/openapi';
import { queryKeys, usePageContext } from '../usePageContext.ts';

export type CodeMatch = ResponseBody<Operation<'/admin/catalog/codes', 'get'>>['items'][number];

export type VariantMatch = CodeMatch & { readonly variantId: string };

export const productIdsOf = (matches: readonly CodeMatch[]): readonly string[] => [
  ...new Set(matches.map((match) => match.productId)),
];

export const variantIdsOf = (matches: readonly CodeMatch[]): ReadonlySet<string> | null =>
  matches.every((match) => match.variantId !== null)
    ? new Set(matches.flatMap((match) => (match.variantId === null ? [] : [match.variantId])))
    : null;

export const singleVariantOf = (matches: readonly CodeMatch[]): VariantMatch | null => {
  if (matches.length !== 1) return null;
  const [only] = matches;
  const variantId = only?.variantId ?? null;
  return only === undefined || variantId === null ? null : { ...only, variantId };
};

export const useCodeLookup = (api: TypedClient) => {
  const { tenantId, queryClient } = usePageContext();
  return (code: string): Promise<readonly CodeMatch[]> =>
    queryClient.query({
      queryKey: queryKeys.catalogCode(tenantId, code),
      queryFn: async () => (await api.get('/admin/catalog/codes', { query: { code } })).items,
      staleTime: staleTimes.catalog,
    });
};
